import { motion } from "framer-motion";

export default function Resume() {
  return (
    <div className="min-h-screen bg-sabriGray text-sabriLight px-6 py-16">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-10"
        >
          <div>
            <h1 className="text-4xl font-title text-sabriBlue">Resume</h1>
            <p className="text-gray-300 mt-2 break-words">
              Sabri Abounozha • Salem, Oregon, United States • 480-300-2122 • Sabri-5200@hotmail.com
            </p>
          </div>

           <a
            className="inline-flex items-center justify-center px-5 py-3 rounded-lg bg-sabriBlue text-white shadow-soft hover:bg-blue-500 transition"
            href={`${process.env.PUBLIC_URL}/Sabri_Abounozha_Resume.pdf`}
            target="_blank"
            rel="noreferrer"
        >
            Download Resume (PDF)
        </a>

        <a
            className="inline-flex items-center justify-center px-5 py-3 rounded-lg bg-white/10 text-sabriLight border border-white/15 hover:bg-white/15 transition"
            href={`${process.env.PUBLIC_URL}/Sabri_Abounozha_Resume.docx`}
            download
        >
            Download Resume (DOCX)
        </a>
        </motion.div>

        {/* EDUCATION */}
        <section className="bg-white/5 border border-white/10 rounded-2xl p-6 mb-6">
          <h2 className="text-2xl font-title mb-4">Education</h2>

          <div className="space-y-4 text-gray-300">
            <div>
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-baseline gap-1">
                <p className="font-semibold text-white">Oregon State University</p>
                <p className="text-sm text-gray-400">Bachelor of Science in Computer Science • Jun. 2026</p>
              </div>
              <p>GPA 3.86 • Summa Cum Laude • Human-Computer Interaction</p>
            </div>

            <div>
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-baseline gap-1">
                <p className="font-semibold text-white">Rio Salado College</p>
                <p className="text-sm text-gray-400">Associate of Art • Aug. 2024</p>
              </div>
              <p>GPA 3.64</p>
            </div>
          </div>
        </section>

        {/* HONORS */}
        <section className="bg-white/5 border border-white/10 rounded-2xl p-6 mb-6">
          <h2 className="text-2xl font-title mb-4">Honors & Leadership</h2>

          <div className="space-y-3 text-gray-300">
            <p>Graduated Summa Cum Laude from Oregon State University.</p>
            <p>Inducted into the NSLS Leadership and Success chapter.</p>
          </div>
        </section>

        {/* PROJECTS */}
        <section className="bg-white/5 border border-white/10 rounded-2xl p-6 mb-6">
          <h2 className="text-2xl font-title mb-4">Computer Science Projects</h2>

          <div className="space-y-5 text-gray-300">
            <div>
              <p className="font-semibold text-white">Tasktix — Senior Capstone Project (Enterprise Productivity App)</p>
              <p className="text-sm text-gray-400 mt-1">Fall 2025 - Spring 2026</p>
              <ul className="list-disc pl-5 space-y-1 mt-2">
                <li>Built a responsive homepage, reusable FeatureBlock UI, and light/dark themed visuals</li>
                <li>Fixed auth-aware navigation so logged-in users route to /list and unauthenticated users route to /about, then added redirect tests</li>
                <li>Added frontend test coverage for homepage rendering, themed images, redirect routing, drawer interactions, and Cypress mobile flow checks</li>
                <li>Collaborated through PRs, code review, and GitHub workflows in an Agile team</li>
              </ul>
            </div>

            <div>
              <p className="font-semibold text-white">Music Streaming Database System</p>
              <ul className="list-disc pl-5 space-y-1 mt-2">
                <li>Designed and implemented a relational database for managing songs and playlists</li>
                <li>Focused on normalization, relational integrity, and efficient SQL queries</li>
              </ul>
            </div>

            <div>
              <p className="font-semibold text-white">Web Application — Playlist Organizer</p>
              <ul className="list-disc pl-5 space-y-1 mt-2">
                <li>Led a team of four to build a CRUD web application for user-generated music lists</li>
                <li>Implemented add/delete/update playlist features and dynamic category filters</li>
              </ul>
            </div>

          </div>
        </section>

        {/* SKILLS */}
        <section className="bg-white/5 border border-white/10 rounded-2xl p-6 mb-6">
          <h2 className="text-2xl font-title mb-4">Skills</h2>

          <div className="text-gray-300 space-y-3">
            <p>
              <span className="font-semibold text-white">Languages/Frameworks:</span>{" "}
              Python, Java, JavaScript, C++, C#, SQL, PHP, Kotlin, GLSL, MATLAB, HTML, Next.js, React, TypeScript, Tailwind CSS, HeroUI, Vitest/Jest
            </p>
            <p>
              <span className="font-semibold text-white">Tools/Frameworks:</span>{" "}
              OpenMP, SIMD, MPI, OpenCL, Git/GitHub, Linux, Docker, REST APIs, Cypress
            </p>
            <p>
              <span className="font-semibold text-white">Other:</span> Fluent in Arabic and English
            </p>
          </div>
        </section>

        {/* EXPERIENCE */}
        <section className="bg-white/5 border border-white/10 rounded-2xl p-6">
          <h2 className="text-2xl font-title mb-4">Work Experience</h2>

          <div className="space-y-5 text-gray-300">
            <div>
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-baseline gap-1">
                <p className="font-semibold text-white">GT Independence — Processing Specialist</p>
                <p className="text-sm text-gray-400">Salem, Oregon • Jun 2026 - Present</p>
              </div>
              <ul className="list-disc pl-5 space-y-1 mt-2">
                <li>Processed return-related emails and accommodated packet requests</li>
                <li>Entered and updated missing packet information</li>
                <li>Made outgoing calls to collect missing information</li>
              </ul>
            </div>

            <div>
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-baseline gap-1">
                <p className="font-semibold text-white">Maxim Integrated — Fab Operator</p>
                <p className="text-sm text-gray-400">Beaverton, Oregon • Sept. 2021 - Dec. 2021</p>
              </div>
              <ul className="list-disc pl-5 space-y-1 mt-2">
                <li>Conducted maintenance for fragile microchips</li>
                <li>Completed detailed work with chemicals</li>
                <li>Transported delicate chips for processing</li>
              </ul>
            </div>
          </div>
        </section>

        <p className="text-sm text-gray-400 mt-10">
            This page reflects my current resume content.
        </p>

      </div>
    </div>
  );
}
